import React from 'react';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="mb-4 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          
          <div className="mb-6">
            <h2 className="text-3xl font-light tracking-wide">
              <span className="text-slate-900">eco</span>
              <span className="text-green-500 font-medium">logical</span>
            </h2>
          </div>
          
          <h1 className="text-3xl font-light text-slate-900">Privacy Policy</h1>
          <p className="text-slate-600 mt-2">Last updated: January 2024</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          
          <h2 className="text-2xl font-medium text-slate-900 mb-4">1. Introduction</h2>
          <p className="text-slate-600 mb-6">
            Ecological Energy Ltd ("we", "our", or "us") is committed to protecting and respecting your privacy. 
            This Privacy Policy explains how we collect, use, and protect your personal data when you visit our 
            website or use our services, in accordance with the UK General Data Protection Regulation (UK GDPR) 
            and the Data Protection Act 2018.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">2. Data Controller</h2>
          <p className="text-slate-600 mb-6">
            Ecological Energy Ltd is the data controller for the purposes of UK data protection law.
            <br /><br />
            <strong>Contact Details:</strong><br />
            Email: <a href="mailto:info@ecological.energy" className="text-slate-600 hover:text-slate-800 no-underline">info@ecological.energy</a><br />
            Address: [Your registered office address]
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">3. Information We Collect</h2>
          <p className="text-slate-600 mb-4">We may collect and process the following personal data:</p>
          <ul className="list-disc pl-6 text-slate-600 mb-6">
            <li><strong>Contact Information:</strong> Name, email address, phone number, company details</li>
            <li><strong>Project Information:</strong> Details about your renewable energy requirements and projects</li>
            <li><strong>Website Usage:</strong> IP address, browser type, pages visited, time spent on site</li>
            <li><strong>Communication Records:</strong> Records of consultations, meetings, and correspondence</li>
          </ul>

          <h2 className="text-2xl font-medium text-slack-900 mb-4">4. How We Use Your Information</h2>
          <p className="text-slate-600 mb-4">We use your personal data for the following purposes:</p>
          <ul className="list-disc pl-6 text-slate-600 mb-6">
            <li>To provide renewable energy consultancy services</li>
            <li>To respond to enquiries and schedule consultations</li>
            <li>To improve our website and services</li>
            <li>To comply with legal obligations</li>
            <li>For legitimate business interests, including marketing (with consent)</li>
          </ul>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">5. Legal Basis for Processing</h2>
          <p className="text-slate-600 mb-6">
            We process personal data based on:
            <br />• <strong>Consent:</strong> Where you have given clear consent
            <br />• <strong>Contract:</strong> To fulfil our contractual obligations
            <br />• <strong>Legal Obligation:</strong> To comply with legal requirements
            <br />• <strong>Legitimate Interest:</strong> For business operations and improvement
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">6. Data Sharing</h2>
          <p className="text-slate-600 mb-6">
            We do not sell your personal data. We may share information with:
            <br />• Trusted service providers who assist in our operations
            <br />• Legal authorities when required by law
            <br />• Professional advisers (solicitors, accountants, insurers)
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">7. Data Retention</h2>
          <p className="text-slate-600 mb-6">
            We retain personal data only for as long as necessary for the purposes outlined in this policy, 
            typically:
            <br />• Contact enquiries: 3 years
            <br />• Client project data: 7 years after project completion
            <br />• Website analytics: 26 months
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">8. Your Rights</h2>
          <p className="text-slate-600 mb-4">Under UK data protection law, you have the right to:</p>
          <ul className="list-disc pl-6 text-slate-600 mb-6">
            <li>Access your personal data</li>
            <li>Rectify inaccurate data</li>
            <li>Erase your data (in certain circumstances)</li>
            <li>Restrict processing</li>
            <li>Data portability</li>
            <li>Object to processing</li>
            <li>Withdraw consent</li>
          </ul>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">9. Cookies</h2>
          <p className="text-slate-600 mb-6">
            Our website uses cookies to improve functionality and user experience. You can manage cookie 
            preferences through your browser settings. Essential cookies are necessary for website operation.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">10. Security</h2>
          <p className="text-slate-600 mb-6">
            We implement appropriate technical and organisational measures to protect your personal data against 
            unauthorised access, alteration, disclosure, or destruction.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">11. Changes to This Policy</h2>
          <p className="text-slate-600 mb-6">
            We may update this Privacy Policy from time to time. Any changes will be posted on this page with 
            an updated revision date.
          </p>

          <h2 className="text-2xl font-medium text-slate-900 mb-4">12. Contact Us</h2>
          <p className="text-slate-600 mb-6">
            If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us:
            <br /><br />
            <strong>Email:</strong> <a href="mailto:info@ecological.energy" className="text-slate-600 hover:text-slate-800 no-underline">info@ecological.energy</a><br />
            <strong>Phone:</strong> +44(0)1392 249230<br />
            <strong>Address:</strong> [Your registered office address]
          </p>

        </div>
      </div>
    </div>
  );
};